import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Mountain } from "lucide-react";

export default function Login() {
  const { login, user, ready } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (ready && user) navigate("/dashboard", { replace: true });
  }, [ready, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-5">
      <div className="w-full max-w-md">
        <div className="mb-8 flex items-center justify-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-[0_4px_14px_rgba(37,99,235,0.35)]">
            <Mountain className="h-5 w-5" strokeWidth={2.5} />
          </span>
          <span className="font-heading text-xl font-extrabold tracking-tight text-slate-900">
            Peak<span className="text-primary">Points</span>
          </span>
        </div>
        <div className="rounded-3xl border border-border bg-card p-8 shadow-[0_24px_60px_-20px_rgba(15,23,42,0.2)]" data-testid="login-card">
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900">Owner Login</h1>
          <p className="mt-1 font-body text-sm text-slate-500">Sign in to review subscribers.</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Email</Label>
              <Input id="email" data-testid="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="owner@peak-points.co" className="h-12 rounded-xl border-slate-200" />
            </div>
            <div>
              <Label htmlFor="password" className="mb-2 block font-heading text-xs font-bold uppercase tracking-[0.15em] text-slate-500">Password</Label>
              <Input id="password" data-testid="login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-12 rounded-xl border-slate-200" />
            </div>
            {error && <p className="font-body text-sm text-destructive" data-testid="login-error">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading} className="w-full rounded-full bg-primary py-6 font-heading font-bold text-primary-foreground hover:bg-blue-700">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </div>
        <p className="mt-6 text-center font-body text-sm text-slate-500">
          <a href="/" className="hover:text-primary">← Back to site</a>
        </p>
      </div>
    </div>
  );
}
